import React, { useState, useEffect, useCallback, useRef } from "react";
import { connect, useDispatch, useSelector } from "react-redux";
import { withRouter } from "react-router-dom";
import moment from "moment";
import LottieLoader from '../../LottieLoader';
import lottieLoaderFile from '../../../files/animation/loading.json';
import RPMCarePlanForm from './RPMCarePlanForm';

// Module-level constant so it's the same array reference across every render. Passing a
// fresh `[]` literal as a prop here would defeat effect dependency checks in the child and
// can trigger an infinite update loop (see savedConditionIds usage below).
const EMPTY_ARRAY = [];

const AddRPMCarePlan = ({ onSave, onCancel, onSubmitForm, profile, patientId: propPatientId, match }) => {
  const patientId = (match && match.params && match.params.id) || propPatientId;
  const dispatch = useDispatch();

  // Get RPM care plan info from Redux store
  const {
    rpmCarePlanInfo,
    ongoingRPMCarePlanRequest,
    ongoingRPMPatientExpectationsRequest = false,
    rpmPatientConditions = [],
    ongoingRPMPatientConditionsRequest = false,
    rpmPatientExpectations = [],
    rpmMedicalNecessityConfig = [],
    ongoingRPMMedicalNecessityConfigRequest = false
  } = useSelector(
    (state) => state.careprovider
  );
  // Removed verbose debug logs to avoid console flooding during renders
  const [formData, setFormData] = useState({
    patientName: null,
    dob: null,
    primaryDiagnosis: null,
    orderingProvider: null,
    npi: null,
    dateRPMInitiated: null,
    lastModifiedDate: null,
    setupEducationDate: null,
    medicalNecessity: null,
    rpmRationale: null,
    consentDate: null,
    deviceInfo: null,
    goals: null,
    careTeam: {},
    escalationPlan: {},
    goalsOfCare: {},
    patientSignature: null,
    providerSignature: null,
    providerName: null
  });

  const [selectedConditionId, setSelectedConditionId] = useState(null);
  const [selectedConditionIds, setSelectedConditionIds] = useState([]);
  const [selectedConditionIcdCodes, setSelectedConditionIcdCodes] = useState([]);
  const [selectedExpectations, setSelectedExpectations] = useState([]);

  const [showConditionError, setShowConditionError] = useState(false);
  const [showIcdCodesError, setShowIcdCodesError] = useState(false);
  const [isIcdCodesValid, setIsIcdCodesValid] = useState(true);
  const carePlanFormRef = useRef(null);
  const initialFetchDone = useRef(false);
  const prevPatientId = useRef(patientId);

  // Clear Redux cache when patientId changes (new user profile opened)
  useEffect(() => {
    if (patientId && patientId !== prevPatientId.current) {
      dispatch({ type: "CLEAR_RPM_MEDICAL_NECESSITY_CONFIG" });
      dispatch({ type: "CLEAR_RPM_PATIENT_CONDITIONS" });
      dispatch({ type: "CLEAR_RPM_PATIENT_EXPECTATIONS" });
      initialFetchDone.current = false;
    }
    prevPatientId.current = patientId;
  }, [patientId, dispatch]);

  // Clear Redux cache when component unmounts (popup closes)
  useEffect(() => {
    return () => {
      dispatch({ type: "CLEAR_RPM_MEDICAL_NECESSITY_CONFIG" });
      dispatch({ type: "CLEAR_RPM_PATIENT_CONDITIONS" });
    };
  }, [dispatch]);

  // Auto-select all conditions when conditions first load
  useEffect(() => {
    if (rpmPatientConditions && rpmPatientConditions.length > 0 && selectedConditionIds.length === 0) {
      setSelectedConditionIds(rpmPatientConditions.map(c => c._id));
    }
    console.log('Fetching expectations and config for conditions:' + JSON.stringify(rpmPatientConditions.map(c => c._id)));
  }, [rpmPatientConditions]);

  // Fetch expectations and medical necessity config when conditions are loaded/selected
  useEffect(() => {
    if (!patientId || initialFetchDone.current) return;
    if (rpmPatientConditions && rpmPatientConditions.length > 0) {
      initialFetchDone.current = true;      
      dispatch({
        type: "DO_GET_RPM_PATIENT_EXPECTATIONS",
        payload: { conditionIds: rpmPatientConditions.map(c => c._id), patientId }
      });

      dispatch({
        type: "DO_GET_RPM_MEDICAL_NECESSITY_CONFIG",
        payload: { conditionIds: rpmPatientConditions.map(c => c._id).join(','), patientId }
      });
    }
  }, [rpmPatientConditions, patientId]);

  // Fetch RPM care plan information on component mount
  useEffect(() => {
    if (patientId) {
      dispatch({
        type: "DO_GET_RPM_CARE_PLAN_INFORMATION",
        payload: { patientId }
      });
    }
  }, [dispatch, patientId]);

  useEffect(() => {
    dispatch({ type: "CLEAR_RPM_PATIENT_CONDITIONS" });
    if (patientId) {
      dispatch({
        type: "DO_GET_RPM_PATIENT_CONDITIONS",
        payload: { patientId }
      });
    }
  }, [dispatch, patientId]);

  // Update form data when API response is received
  useEffect(() => {
    if (rpmCarePlanInfo) {
      const userProfile = rpmCarePlanInfo.userProfile || {};
      const patientConsent = rpmCarePlanInfo.patientConsent || {};
      const patientExpectations = rpmCarePlanInfo.patientExpectations || {};
      const careTeam = rpmCarePlanInfo.careTeam || {};
      const devices = rpmCarePlanInfo.devices || [];

      // Format patient name
      const patientName = userProfile.fname && userProfile.lname
        ? `${userProfile.fname} ${userProfile.lname}`
        : "";

      // Format DOB (American date format MM/DD/YYYY)
      const dob = userProfile.bday
        ? moment(userProfile.bday).format("MM/DD/YYYY")
        : "";

      // Format consent date (American date format MM/DD/YYYY) — falls back to
      // carePlanCreatedDate (RPM ONB note date, or first chart note date) when no
      // verbal consent note exists.
      const consentDate = patientConsent.rpmVerbalNoteDate
        ? moment(patientConsent.rpmVerbalNoteDate).format("MM/DD/YYYY")
        : (rpmCarePlanInfo.carePlanCreatedDate ? moment(rpmCarePlanInfo.carePlanCreatedDate).format("MM/DD/YYYY") : "");

      // Format devices info
      // A replacement device adds a new record without removing the old one, so the
      // same device can repeat. Label each with its device type, since two different
      // devices can share a name, then collapse the identical labels.
      const deviceLabel = (d) => {
        const name = (d.device && d.device.name) || 'Device';
        const type = d.device && d.device.deviceType && d.device.deviceType.description;
        return type ? `${name} (${type})` : name;
      };
      const deviceInfo = devices.length > 0
        ? [...new Set(devices.map(deviceLabel))].join(", ")
        : "";
      const baseImageUrl = process.env.REACT_APP_VIDEO_CALL_REQUEST;
      const patientSig = userProfile.patientSignature
        ? `${baseImageUrl}/${userProfile.patientSignature}`
        : undefined;
      const providerSig = userProfile.providerSignature
        ? `${baseImageUrl}/${userProfile.providerSignature}`
        : undefined;
      const medicalDirectorSig = rpmCarePlanInfo.medicalDirectorSignature
        ? `${baseImageUrl}/${rpmCarePlanInfo.medicalDirectorSignature}`
        : undefined;

      const dateRPMInitiated = rpmCarePlanInfo.carePlanCreatedDate
        ? moment(rpmCarePlanInfo.carePlanCreatedDate).format("MM/DD/YYYY")
        : moment().format("MM/DD/YYYY");

      setFormData({
        patientName: patientName,
        dob: dob,
        primaryDiagnosis: userProfile.primaryDiagnosis || "",
        orderingProvider: userProfile.mainCareProvider || "",
        npi: userProfile.NPI && userProfile.NPI !== "N/A" ? userProfile.NPI : "",
        dateRPMInitiated: dateRPMInitiated,
        // Shows the same date as Date RPM Initiated.
        lastModifiedDate: dateRPMInitiated,
        // Welcome Call Completed note date, or the patient's first upload date for
        // patients enrolled before that note existed. Computed by the API.
        setupEducationDate: rpmCarePlanInfo.setupEducationDate ? moment(rpmCarePlanInfo.setupEducationDate).format("MMMM DD, YYYY") : "",
        medicalNecessity: patientExpectations.medicalNecessity,
        rpmRationale: patientExpectations.rationaleRPM,
        consentDate: consentDate,
        deviceInfo: deviceInfo,
        careTeam: careTeam,
        escalationPlan: rpmCarePlanInfo.escalationPlan || {},
        goalsOfCare: rpmCarePlanInfo.goalsOfCare || {},
        patientSignature: patientSig,
        providerSignature: providerSig,
        providerName: userProfile.mainCareProvider || "",
        medicalDirectorName: rpmCarePlanInfo.medicalDirectorName || "",
        medicalDirectorNPI: rpmCarePlanInfo.medicalDirectorNPI || "",
        medicalDirectorSignature: medicalDirectorSig,
        hasVerbalConsentNote: !!rpmCarePlanInfo.hasVerbalConsentNote
      });
    }
  }, [rpmCarePlanInfo]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Handle condition change - track all selected conditions
  const handleConditionChange = (selectedConditions) => {
    if (Array.isArray(selectedConditions)) {
      const conditionIds = selectedConditions.map(c => c._id || c);
      setSelectedConditionIds(conditionIds);
      if (conditionIds.length > 0) {
        setShowConditionError(false);
        // Fetch config when user changes conditions
        dispatch({
          type: "DO_GET_RPM_MEDICAL_NECESSITY_CONFIG",
          payload: { conditionIds: conditionIds.join(','), patientId }
        });
      }
    } else if (selectedConditions && selectedConditions._id) {
      setSelectedConditionIds([selectedConditions._id]);
      setShowConditionError(false);
    } else if (typeof selectedConditions === 'string') {
      setSelectedConditionIds([selectedConditions]);
      setShowConditionError(false);
    } else {
      setSelectedConditionIds([]);
    }
  };

  const handleConditionIcdCodesChange = (rows) => {
    setSelectedConditionIcdCodes(rows);
  };

  const handleSubmit = useCallback((e) => {
    e.preventDefault();

    // Validation: block save if no conditions selected
    if (selectedConditionIds.length === 0) {
      setShowConditionError(true);
      if (carePlanFormRef.current?.scrollToConditionError) {
        carePlanFormRef.current.scrollToConditionError();
      }
      return;
    }
    setShowConditionError(false);

    // Validation: block save if any ICD-10 diagnosis row is incomplete (e.g. category
    // picked but no code chosen) — otherwise it would silently be dropped from the save.
    if (!isIcdCodesValid) {
      setShowIcdCodesError(true);
      if (carePlanFormRef.current?.scrollToIcdCodesError) {
        carePlanFormRef.current.scrollToIcdCodesError();
      }
      return;
    }
    setShowIcdCodesError(false);

    // IMPORTANT: If expectations are still loading, warn the user
    if (ongoingRPMPatientExpectationsRequest) {
      alert('Please wait for expectations to load before submitting.');
      return;
    }

    // Prepare data in the format expected by the API, using original objects and updating with form data
    const apiData = {
      patientId: patientId,
      userProfile: {
        mainCareProvider: rpmCarePlanInfo?.userProfile?.mainCareProviderId,
        primaryDiagnosis: rpmCarePlanInfo?.userProfile?.primaryDiagnosis,
        careCoordinator: rpmCarePlanInfo?.userProfile?.careCoordinator,
        NPI: rpmCarePlanInfo?.userProfile?.NPI,
      },
      patientConsent: {
        rpmVerbalNoteDate: rpmCarePlanInfo?.patientConsent?.rpmVerbalNoteDate
      },
      patientExpectations: {
        medicalNecessity: rpmCarePlanInfo?.patientExpectations?.medicalNecessity || false,
        rationaleRPM: rpmCarePlanInfo?.patientExpectations?.rationaleRPM || false,
        expectationThreshold: selectedExpectations
      },
      devices: rpmCarePlanInfo?.devices?.map(d => ({
        device: d.device?._id,
        serial: d.serial
      })) || [],
      careTeam: {
        ...rpmCarePlanInfo?.careTeam
      },
      escalationPlan: rpmCarePlanInfo?.escalationPlan._id,
      goalsOfCare: rpmCarePlanInfo?.goalsOfCare._id,
      // Include the conditions and expectations fields
      conditionIds: selectedConditionIds,
      // Include the selected ICD-10 diagnosis codes (category -> code, or manual entry)
      conditionIcdCodes: selectedConditionIcdCodes,
      // Include RPM medical necessity config IDs
      rpmMedicalNecessity: rpmMedicalNecessityConfig.map(config => config._id)
    };


    // Dispatch the save or update action
    dispatch({
      type: "DO_SAVE_RPM_CARE_PLAN",
      payload: apiData
    });

    // Call onSave callback if provided
    if (onSave) {
      onSave(apiData);
    }

    // Close the modal after save
    if (onCancel) {
      onCancel();
    }
  }, [selectedConditionIds, selectedConditionIcdCodes, isIcdCodesValid, selectedExpectations, ongoingRPMPatientExpectationsRequest, patientId, rpmCarePlanInfo, dispatch, onSave, onCancel]);

  // Method to submit form from parent component
  const submitForm = useCallback(() => {
    handleSubmit(new Event('submit'));
  }, [handleSubmit]);

  // Call onSubmitForm callback when parent triggers it
  useEffect(() => {
    if (onSubmitForm) {
      onSubmitForm(submitForm);
    }
  }, [onSubmitForm, submitForm]);

  return (
    <div className="rpm-care-plan-view">
      {/* LottieLoader at top of view */}
      {ongoingRPMCarePlanRequest && (
        <div className="text-center" style={{ marginTop: "50px", marginBottom: "50px" }}>
          <LottieLoader
            loadLoop={true}
            source={lottieLoaderFile}
            Lheight={150}
            Lwidth={150}
            subTitle={"Please Wait ..."}
          />
        </div>
      )}

      {!ongoingRPMCarePlanRequest && !rpmCarePlanInfo && (
        <div className="text-center my-3">
          <p>No RPM Care Plan information found for this patient.</p>
        </div>
      )}

      {!ongoingRPMCarePlanRequest && rpmCarePlanInfo && (
        <>
          {ongoingRPMPatientConditionsRequest && (
            <p style={{ textAlign: 'center', color: '#666' }}>Loading conditions...</p>
          )}
          <RPMCarePlanForm
            ref={carePlanFormRef}
            showConditionError={showConditionError}
            showIcdCodesError={showIcdCodesError}
            formData={formData}
            onInputChange={handleInputChange}
            onSubmit={handleSubmit}
            isEditing={true}
            showButtons={false}
            patientId={patientId}
            onConditionChange={handleConditionChange}
            onConditionIcdCodesChange={handleConditionIcdCodesChange}
            onConditionIcdCodesValidityChange={setIsIcdCodesValid}
            onExpectationsChange={setSelectedExpectations}
            selectedConditionId={selectedConditionId}
            selectedExpectations={selectedExpectations}
            savedConditionIds={EMPTY_ARRAY}
            savedConditionIcdCodes={EMPTY_ARRAY}
            rpmMedicalNecessityConfig={rpmMedicalNecessityConfig}
            ongoingRPMMedicalNecessityConfigRequest={ongoingRPMMedicalNecessityConfigRequest}
          />
        </>
      )}
    </div>
  );
};

const mapStateToProps = (state) => {
  return {
    profile: state.user.profileOwner
  };
};

export default connect(mapStateToProps)(withRouter(AddRPMCarePlan));